import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { FIELD_KEYS, FIELD_LABELS } from "../lib/api";
import { useDrive } from "../context/DriveContext";
import DriveRequired from "../components/DriveRequired";
import {
  PaperPlaneTilt,
  FolderOpen,
  CheckCircle,
  XCircle,
  WarningCircle,
  GearSix,
  ArrowsClockwise,
} from "@phosphor-icons/react";

export default function HomePage() {
  const { connected, checking } = useDrive();
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [form, setForm] = useState({
    code: "",
    name: "",
    price: "",
    quantity: "",
    producer: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);

  const loadFiles = useCallback(async () => {
    setLoadingFiles(true);
    try {
      const { data } = await api.get("/files");
      setFiles(data);
    } catch (e) {
      console.error("load files failed", e);
      toast.error("Не удалось загрузить список файлов");
    } finally {
      setLoadingFiles(false);
    }
  }, []);

  useEffect(() => {
    if (connected) loadFiles();
    else setLoadingFiles(false);
  }, [connected, loadFiles]);

  const grouped = useMemo(() => {
    const by = {};
    for (const f of files) {
      const k = f.folder_name || "Без папки";
      (by[k] = by[k] || []).push(f);
    }
    return Object.entries(by).sort(([a], [b]) => a.localeCompare(b));
  }, [files]);

  if (checking) {
    return (
      <div className="p-6 lg:p-10">
        <div className="mono-label">ЗАГРУЗКА...</div>
      </div>
    );
  }

  if (!connected) {
    return (
      <div className="p-6 lg:p-10 max-w-4xl">
        <div className="mono-label mb-2">СЕКЦИЯ / ПРАЙСЫ</div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Добавить строку в прайсы</h1>
        <p className="text-sm text-[#52525b] mt-2 max-w-2xl">
          Раздел работает с прайс-листами на Google Drive — нужен доступ.
        </p>
        <div className="mt-6">
          <DriveRequired description="Подключите Google Drive один раз, и этот раздел заработает. Остальные разделы портала доступны без подключения." />
        </div>
      </div>
    );
  }

  const mappedFiles = files.filter((f) => f.mapped);
  const allSelected = mappedFiles.length > 0 && mappedFiles.every((f) => selected.has(f.id));

  const toggle = (id) =>
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const toggleFolder = (folderFiles) => {
    const mapped = folderFiles.filter((f) => f.mapped);
    const allOn = mapped.every((f) => selected.has(f.id));
    setSelected((s) => {
      const n = new Set(s);
      for (const f of mapped) {
        if (allOn) n.delete(f.id);
        else n.add(f.id);
      }
      return n;
    });
  };

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(mappedFiles.map((f) => f.id)));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.code.trim()) {
      toast.error("Код ЕГК обязателен");
      return;
    }
    if (selected.size === 0) {
      toast.error("Выберите хотя бы один прайс");
      return;
    }
    setSubmitting(true);
    setResults(null);
    try {
      const { data } = await api.post("/append", {
        file_ids: Array.from(selected),
        data: form,
      });
      setResults(data);
      if (data.ok_count === data.total) {
        toast.success(`Добавлено во все ${data.total} файлов`);
      } else if (data.ok_count > 0) {
        toast.warning(`Успешно: ${data.ok_count} из ${data.total}`);
      } else {
        toast.error("Не удалось добавить ни в один файл");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Ошибка добавления");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid grid-rows-[auto_1fr] h-screen">
      {/* Top: form */}
      <div className="bg-white border-b border-[#d4d4d8] p-6 lg:p-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="mono-label mb-1">ACTION / APPEND ROW</div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">
              Добавить строку в прайсы
            </h1>
          </div>
          <div className="text-right">
            <div className="mono-label">ВЫБРАНО</div>
            <div className="font-mono text-xl font-semibold" data-testid="selected-count">
              {selected.size} <span className="text-[#52525b]">/ {mappedFiles.length}</span>
            </div>
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid="add-product-form"
          className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4"
        >
          {FIELD_KEYS.map((k) => (
            <div key={k} className="space-y-1.5">
              <label className="mono-label block" htmlFor={`f-${k}`}>
                {FIELD_LABELS[k]}
                {k === "code" && <span className="text-[#FF2A00]"> *</span>}
              </label>
              <input
                id={`f-${k}`}
                data-testid={`add-product-${k}-input`}
                value={form[k]}
                onChange={(e) => setForm((s) => ({ ...s, [k]: e.target.value }))}
                placeholder={k === "code" ? "78991" : ""}
                className="w-full font-mono text-sm border border-[#d4d4d8] px-3 py-2 bg-white focus:outline-none focus:border-[#0047FF] focus:ring-1 focus:ring-[#0047FF]"
              />
            </div>
          ))}
          <div className="col-span-full flex flex-wrap items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={submitting}
              data-testid="add-product-submit"
              className="inline-flex items-center gap-2 bg-[#0047FF] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#0036CC] transition-colors duration-150 disabled:opacity-50"
            >
              <PaperPlaneTilt size={14} weight="bold" />
              {submitting ? "Добавление..." : `Добавить в ${selected.size} файл(ов)`}
            </button>
            <button
              type="button"
              onClick={() => {
                setForm({ code: "", name: "", price: "", quantity: "", producer: "" });
                setResults(null);
              }}
              className="text-sm border border-[#18181b] text-[#18181b] bg-transparent px-3 py-2 hover:bg-[#18181b] hover:text-white transition-colors duration-150"
            >
              Очистить
            </button>
            <div className="text-xs text-[#52525b]">
              * В файлы, где нет столбца «Производитель» или «Количество», они не попадут — это настраивается в маппинге.
            </div>
          </div>
        </form>
      </div>

      {/* Bottom: file picker + results */}
      <div className="overflow-y-auto bg-[#f5f5f4] p-6 lg:p-8">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="mono-label mb-1">TARGET FILES</div>
            <h2 className="text-xl font-medium tracking-tight">Куда добавить</h2>
            <p className="text-xs text-[#52525b] mt-1">
              Показаны только файлы с настроенным маппингом. Остальные — в разделе «Прайс-листы».
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadFiles}
              data-testid="refresh-files"
              className="inline-flex items-center gap-1 border border-[#d4d4d8] bg-white px-3 py-1.5 text-xs hover:bg-[#18181b] hover:text-white hover:border-[#18181b] transition-colors duration-150"
            >
              <ArrowsClockwise size={12} weight="bold" />
              Обновить
            </button>
            <button
              onClick={toggleAll}
              disabled={mappedFiles.length === 0}
              data-testid="toggle-all-files"
              className="text-xs border border-[#18181b] bg-transparent text-[#18181b] px-3 py-1.5 hover:bg-[#18181b] hover:text-white transition-colors duration-150 disabled:opacity-40"
            >
              {allSelected ? "Снять все" : "Выбрать все"}
            </button>
          </div>
        </div>

        {loadingFiles && <div className="mt-6 mono-label">ЗАГРУЗКА...</div>}

        {!loadingFiles && files.length === 0 && (
          <div className="mt-6 bg-white border border-[#d4d4d8] p-8 text-center" data-testid="home-empty">
            <FolderOpen size={32} weight="duotone" className="mx-auto text-[#52525b]" />
            <h3 className="text-lg font-medium mt-3">Прайс-листы не найдены</h3>
            <p className="text-sm text-[#52525b] mt-1">
              Укажите корневую папку в настройках и запустите сканирование.
            </p>
            <Link
              to="/settings"
              className="inline-flex items-center gap-2 mt-4 bg-[#0047FF] text-white px-4 py-2 text-sm hover:bg-[#0036CC] transition-colors duration-150"
            >
              <GearSix size={14} weight="bold" />
              Перейти в настройки
            </Link>
          </div>
        )}

        {!loadingFiles && files.length > 0 && (
          <div className="mt-5 space-y-6">
            {grouped.map(([folder, folderFiles]) => {
              const mapped = folderFiles.filter((f) => f.mapped);
              const allFolderSelected = mapped.length > 0 && mapped.every((f) => selected.has(f.id));
              return (
                <section key={folder} className="bg-white border border-[#d4d4d8]" data-testid={`site-group-${folder}`}>
                  <header className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-[#d4d4d8] bg-[#f5f5f4]">
                    <div className="flex items-center gap-2">
                      <FolderOpen size={16} weight="duotone" className="text-[#0047FF]" />
                      <span className="font-medium">{folder}</span>
                      <span className="mono-label">
                        {mapped.length}/{folderFiles.length} готовых
                      </span>
                    </div>
                    {mapped.length > 0 && (
                      <button
                        onClick={() => toggleFolder(folderFiles)}
                        className="text-xs border border-[#18181b] bg-transparent text-[#18181b] px-2.5 py-1 hover:bg-[#18181b] hover:text-white transition-colors duration-150"
                      >
                        {allFolderSelected ? "Снять все" : "Выбрать все"}
                      </button>
                    )}
                  </header>

                  <ul className="divide-y divide-[#d4d4d8]">
                    {folderFiles.map((f) => {
                      const checked = selected.has(f.id);
                      const disabled = !f.mapped;
                      return (
                        <li
                          key={f.id}
                          className={`px-4 py-2.5 flex items-center gap-3 ${
                            disabled ? "opacity-50" : "cursor-pointer hover:bg-[#f5f5f4]"
                          } transition-colors duration-150`}
                          onClick={() => !disabled && toggle(f.id)}
                          data-testid={`file-pick-${f.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={disabled}
                            readOnly
                            className="accent-[#0047FF] w-4 h-4"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm truncate">{f.file_name}</div>
                            <div className="text-xs text-[#52525b] font-mono">
                              {f.fmt?.toUpperCase()} · {f.total_cols ?? "?"} столбцов
                              {f.subpath && <> · /{f.subpath}</>}
                            </div>
                          </div>
                          {!f.mapped && (
                            <span className="inline-flex items-center gap-1 text-[#FF2A00] text-xs">
                              <WarningCircle size={12} weight="fill" />
                              маппинг не задан
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </section>
              );
            })}
          </div>
        )}

        {/* Results */}
        {results && (
          <section
            className="mt-8 bg-white border border-[#18181b] hard-shadow"
            data-testid="result-log-panel"
          >
            <header className="flex items-center justify-between px-4 py-2.5 border-b border-[#d4d4d8]">
              <div>
                <div className="mono-label">РЕЗУЛЬТАТ</div>
                <div className="text-sm">
                  <span className="font-mono font-semibold">
                    {results.ok_count} / {results.total}
                  </span>{" "}
                  успешно
                </div>
              </div>
              <button
                onClick={() => setResults(null)}
                className="text-xs border border-[#d4d4d8] bg-white px-2 py-1 hover:bg-[#18181b] hover:text-white hover:border-[#18181b] transition-colors duration-150"
              >
                Скрыть
              </button>
            </header>
            <ul className="divide-y divide-[#d4d4d8]">
              {results.results.map((r) => (
                <li
                  key={r.file_id}
                  className="px-4 py-2.5 flex items-center gap-3 text-sm"
                  data-testid={`result-${r.file_id}`}
                >
                  {r.ok ? (
                    <CheckCircle size={16} weight="fill" className="text-[#00994C]" />
                  ) : (
                    <XCircle size={16} weight="fill" className="text-[#FF2A00]" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{r.file_name || r.file_id}</div>
                    <div className="text-xs text-[#52525b] font-mono">
                      {r.folder_name}
                      {r.error && <> · ошибка: {r.error}</>}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}
